import type { MouseEvent } from "react";
import React, { useEffect, useRef, useState } from "react";
import { isMobile, isSafari } from "react-device-detect";

import Button from "@components/atoms/Button";

import useLayout from "@hooks/reducerHooks/useLayout";

import type { IWordList } from "@utils/canvas";
import { changeColor, detectMobileDevice, findCurrentArea } from "@utils/canvas";
import { getImageWithSize } from "@utils/captcha";
import { NOTIFICATIONS } from "@utils/notifications";

const DPR = detectMobileDevice() ? 5 : 10;

const CANVAS_WIDTH = 330 * DPR;
const CANVAS_HEIGHT = 292 * DPR;

const CANVAS_WIDTH_ORIGIN = 330;
const CANVAS_HEIGHT_ORIGIN = 292;

const WORD_GAP = 5 * DPR;

const MAX_IMAGE_X = CANVAS_WIDTH - 20 * DPR;
const MAX_IMAGE_Y = CANVAS_HEIGHT - 20 * DPR;

const IMAGE_HEIGHT = 82;

interface IProps {
  setNeedCaptcha: (value: boolean) => void;
}

function Captcha({ setNeedCaptcha }: IProps) {
  const { openToast } = useLayout();

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [wordList, setWordList] = useState<IWordList[]>([]);
  const [selectedList, setSelectedList] = useState<IWordList[]>([]);
  const [selectedWord, setSelectedWord] = useState<IWordList | null>(null); // 마우스 down과 up의 위치가 다를때 다른 단어가 선택되는것을 방지

  useEffect(() => {
    if (!canvasRef.current) return;

    canvasRef.current.height = CANVAS_HEIGHT;
    canvasRef.current.width = CANVAS_WIDTH;

    canvasRef.current.style.height = `${CANVAS_HEIGHT_ORIGIN}px`;
    canvasRef.current.style.width = `${CANVAS_WIDTH_ORIGIN}px`;

    getRandomList();
  }, []);

  useEffect(() => {
    if (wordList.length) {
      drawTextAll();
    }
  }, [wordList]);

  /** 단어 불러오기 + 좌표계산 */
  const getRandomList = async () => {
    const { base, image } = await getImageWithSize(IMAGE_HEIGHT, DPR);

    const totalList: IWordList[] = [];

    //  T,U,N,E가 몇번째 줄에 들어갈지 랜덤으로 지정
    const tuneLineIndex = [];
    while (tuneLineIndex.length < 4) {
      const randomIndex = Math.floor(Math.random() * 3);
      const getMax = tuneLineIndex.filter((item) => item === randomIndex);
      if (getMax.length >= 2) continue; // 한줄에 2개 이상 들어가버리면 다 못들어갈 가능성 큼

      tuneLineIndex.push(randomIndex);
    }

    // T,U,N,E를 line 순으로 정렬
    tuneLineIndex.sort((a, b) => a - b);

    let imageX = 20 * DPR;
    let imageY = 20 * DPR;
    let line = 0; // 몇번째 줄인지

    let remainWidth = MAX_IMAGE_X - 20 * DPR; // 현재 위치한 line에 남아있는 width

    while (imageY <= MAX_IMAGE_Y) {
      // (T, U, N, E) 먼저 삽입
      const tuneValue = tuneLineIndex.filter((item) => item === line);
      if (tuneValue.length) {
        for (let i = 0; i < tuneValue.length; i++) {
          totalList.push({
            ...base[i],
            x1: imageX,
            y1: imageY,
            x3: imageX + base[i].width,
            y3: imageY + base[i].height,
            colorType: "grey",
          });

          // 옆에 배치
          imageX += (base[i].width || 0) + WORD_GAP;
          remainWidth -= base[i].width + WORD_GAP;
        }

        base.splice(0, tuneValue.length);
        tuneLineIndex.splice(0, tuneValue.length);
      }

      // (T, U, N, E)을 제외한 나머지 단어들 랜덤으로 배치
      const randomImage = image[Math.floor(Math.random() * image.length)];

      if (remainWidth > image[0].width) {
        if (remainWidth >= randomImage.width) {
          totalList.push({
            ...randomImage,
            x1: imageX,
            y1: imageY,
            x3: imageX + randomImage.width,
            y3: imageY + randomImage.height,
            colorType: "grey",
          });

          // 중복 방지를 위해 선택된 단어는 리스트에서 제거
          const randomIndex = image.findIndex((item) => item === randomImage);
          image.splice(randomIndex, 1);

          // 옆에 배치
          imageX += randomImage.width + WORD_GAP;
          remainWidth -= randomImage.width + WORD_GAP;
        }
      } else {
        // 같은 라인에 남은 공간이 없으면 다음줄로 이동
        imageX = 20 * DPR;
        imageY = imageY + randomImage.height + WORD_GAP;
        remainWidth = MAX_IMAGE_X - 20 * DPR;

        line++;

        continue;
      }
    }

    // 같은 라인에 있는 단어들끼리 묶기 (같은 y1 좌표를 갖고 있음)
    const getY1List = totalList.map((item) => item.y1);
    const getY1 = getY1List.filter((element, index) => getY1List.indexOf(element) === index);

    // T,U,N,E 이 제일 앞으로 오게끔 해놔서 랜덤으로 다시 섞기 + 가운대 정렬
    const firstLine = textToCenter(totalList.filter((item) => item.y1 === getY1[0]));
    const secondLine = textToCenter(totalList.filter((item) => item.y1 === getY1[1]));
    const lastLine = textToCenter(totalList.filter((item) => item.y1 === getY1[2]));
    setWordList([...firstLine, ...secondLine, ...lastLine]);
  };

  /** 가운대 정렬 + 순서 한번 더 섞기 */
  const textToCenter = (list: IWordList[]) => {
    const totalWidth = list.map((item) => item.width).reduce((a, b) => a + b) + (list.length - 1) * WORD_GAP;
    const addX = Math.round((CANVAS_WIDTH - totalWidth) / 2);
    const newList: IWordList[] = [];
    let imageX = addX;

    list
      .sort(() => Math.random() - 0.5)
      .forEach((item) => {
        newList.push({
          ...item,
          x1: imageX,
          y1: item.y1,
          x3: imageX + item.width,
          y3: item.y1 + item.height,
        });
        imageX += item.width + WORD_GAP;
      });

    return newList;
  };

  /** 단어 그리기 */
  const drawTextAll = () => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    if (!isSafari) ctx.reset();
    ctx.beginPath();
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.scale(DPR, DPR);

    wordList.forEach((item) => {
      const fillImgData = changeColor(item);
      ctx.putImageData(fillImgData, item.x1, item.y1);
    });

    ctx.closePath();
  };

  const drawFocusText = (focusText: IWordList) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    // 배경 덧칠
    // const background = new OffscreenCanvas(focusText.width, focusText.height);
    const background = document.createElement("canvas");
    background.width = focusText.width;
    background.height = focusText.height;
    const ctxBackground = background.getContext("2d");
    if (!ctxBackground) return;

    ctxBackground.beginPath();
    ctxBackground.clearRect(0, 0, focusText.width, focusText.height);
    ctxBackground.fillStyle = "#f4f4f5";
    ctxBackground.fillRect(0, 0, focusText.width, focusText.height);
    ctxBackground.closePath();

    const { data: backgroundData } = ctxBackground.getImageData(0, 0, focusText.width, focusText.height);
    const backgroundImageData = new ImageData(new Uint8ClampedArray(backgroundData), focusText.width, focusText.height);

    // 클릭했을 때 0.9 배 작아지기
    const newWidth = Math.floor(focusText.width * 0.9);
    const newHeight = Math.floor(focusText.height * 0.9);

    const widthGap = Math.floor((focusText.width - newWidth) / 2);
    const heightGap = Math.floor((focusText.height - newHeight) / 2);

    // const offScreen = new OffscreenCanvas(newWidth, newHeight);
    const offScreen = document.createElement("canvas");
    offScreen.width = newWidth;
    offScreen.height = newHeight;

    const ctxScreen = offScreen.getContext("2d");
    if (!ctxScreen) return;

    ctxScreen.beginPath();
    ctxScreen.clearRect(0, 0, newWidth, newHeight);
    ctxScreen.drawImage(focusText.imageData, 0, 0, newWidth, newHeight);
    ctxScreen.closePath();

    const { data } = ctxScreen.getImageData(0, 0, newWidth, newHeight);
    const fillImgData = new ImageData(new Uint8ClampedArray(data), newWidth, newHeight);

    ctx.putImageData(backgroundImageData, focusText.x1, focusText.y1); // 배경 칠하고
    ctx.putImageData(fillImgData, focusText.x1 + widthGap, focusText.y1 + heightGap); // 0.9배 작아지기
  };

  /** 캔버스 마우스 이벤트 */
  const handleMouseEvent = (e: MouseEvent<HTMLCanvasElement>) => {
    const { type } = e;

    const clientRect = canvasRef.current?.getBoundingClientRect();

    const offsetX = e.clientX - (clientRect?.x || 0);
    const offsetY = e.clientY - (clientRect?.y || 0);

    const result = findCurrentArea({ wordList, offsetX, offsetY });
    if (!result) {
      document.body.style.cursor = "default";

      if (selectedWord) {
        const findData = selectedList.find((item) => item.name === selectedWord.name);
        if (findData) {
          removeWordList();
        } else {
          setNewWordList();
        }
      }

      return;
    }

    if (type === "mouseout") {
      document.body.style.cursor = "default";
    }

    // if (type === 'mousemove') {
    //   document.body.style.cursor = 'pointer';
    // }

    if (type === "mousedown" || type === "pointerdown") {
      const findData = selectedList.find((item) => item.name === result.name);
      if (findData) {
        drawFocusText(result);
        setSelectedWord(result);
      } else {
        if (selectedList.length > 3) {
          openToast(NOTIFICATIONS.WARN_SELECT_LETTER);
          return;
        }

        drawFocusText(result);
        setSelectedWord(result);
      }
    }

    if (type === "mouseup" || type === "mousemove" || type === "pointermove" || type === "pointerup") {
      if (type === "mousemove") document.body.style.cursor = "pointer";

      if (!selectedWord) return;

      const findData = selectedList.find((item) => item.name === selectedWord.name);
      if (findData) {
        // 선택 해제
        removeWordList();
      } else {
        // 4개 이상 선택
        if (selectedList.length > 3) {
          openToast(NOTIFICATIONS.WARN_SELECT_LETTER);
          return;
        }

        setNewWordList();
      }
    }
  };

  /** 검은색으로 칠할 단어 세팅 */
  const setNewWordList = () => {
    if (selectedWord) {
      const newWordList: IWordList[] = wordList.map((item) =>
        item.name === selectedWord.name ? { ...item, colorType: "black" } : { ...item },
      );
      setSelectedList([...selectedList, selectedWord]);
      setWordList(newWordList);
      setSelectedWord(null);
    }
  };

  /** 재선택시 선택해제 */
  const removeWordList = () => {
    if (selectedWord) {
      const newWordList: IWordList[] = wordList.map((item) =>
        item.name === selectedWord.name ? { ...item, colorType: "grey" } : { ...item },
      );
      setWordList(newWordList);
      const newSelectedData = selectedList.filter((item) => item.name !== selectedWord.name);
      setSelectedList([...newSelectedData]);
      setSelectedWord(null);
    }
  };

  const handleCheckData = () => {
    const answer = ["E", "N", "T", "U"]; // 'T', 'U', 'N', 'E' 알파벳순으로 정렬
    const words = selectedList.map((item) => item.name).sort();

    const isEqual = JSON.stringify(answer) === JSON.stringify(words);
    if (isEqual) {
      sessionStorage.setItem("CAPTCHA", "true");
      openToast(NOTIFICATIONS.SUCCESS_SELECT_LETTER);
      setTimeout(() => {
        setNeedCaptcha(false);
      }, 800);
    } else {
      openToast(NOTIFICATIONS.WARN_SELECT_WRONG_LETTER);
      setSelectedList([]);
      getRandomList();
    }
  };

  return (
    <>
      <div className="flex h-[292px] flex-col items-center justify-center gap-5 rounded-[10px] bg-zinc-100">
        {isMobile ? (
          <canvas
            onPointerDown={handleMouseEvent}
            onPointerMove={handleMouseEvent}
            onPointerUp={handleMouseEvent}
            ref={canvasRef}
          />
        ) : (
          <canvas
            onMouseDown={handleMouseEvent}
            onMouseMove={handleMouseEvent}
            onMouseOut={handleMouseEvent}
            onMouseUp={handleMouseEvent}
            ref={canvasRef}
          />
        )}
      </div>
      <Button disabled={selectedList.length < 4} onClick={handleCheckData} styleType="primary" variant="contained">
        Submit
      </Button>
    </>
  );
}

export default Captcha;
